import React, { Component } from 'react';
import { Form, Field } from 'react-final-form';
import config from '../config';

class SignUpBox extends Component {
  constructor(props) {
    super(props);

    this.state = {
      errorMessage: '',
      errorMessageShow: false,
      showAllErrors: false,
      isSubmitting: false,
      showPassword: false,
      showPasswordConfirmation: false,
      passwordMatchFeedback: null,
    };

    this.passwordMatchTimer = null;
  }

  componentWillUnmount() {
    if (this.passwordMatchTimer) {
      clearTimeout(this.passwordMatchTimer);
    }
  }

  normalizeEmail = (email) => {
    return email.toString().trim().toLowerCase();
  };

  cleanEmailInput = (email) => {
    return email.toString().replace(/\s+/g, '').toLowerCase();
  };

  isValidEmail = (email) => {
    const normalizedEmail = this.normalizeEmail(email);

    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
  };

  onSubmit = (values, form) => {
    const {
      setIsUserSignedIn,
      setUser,
      setDoctors,
      setInsurances,
      setBodilyParts,
      setOriginalDoctors,
      onSuccessfulSignUp,
    } = this.props;

    const csrfToken = localStorage.getItem('csrfToken');
    const normalizedEmail = this.normalizeEmail(values.email || '');

    this.setState({
      isSubmitting: true,
      errorMessage: '',
      errorMessageShow: false,
    });

    fetch(`${config.API_BASE_URL}/api/v1/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      credentials: 'include',
      body: JSON.stringify({
        user: {
          email: normalizedEmail,
          username: values.username,
          password: values.password,
          password_confirmation: values.passwordConfirmation,
          accepted_terms: Boolean(values.acceptedTerms),
        },
      }),
    })
      .then((res) => {
        return res.json().then((response) => ({
          ok: res.ok,
          status: res.status,
          response,
        }));
      })
      .then(({ ok, response }) => {
        if (ok) {
          console.log('response /signup', response);

          setIsUserSignedIn(true);
          setUser(response.user);
          setDoctors(response.doctors || []);
          setOriginalDoctors(response.doctors || []);
          setInsurances(response.insurances || []);
          setBodilyParts(response.bodily_parts || []);

          if (form) {
            form.reset();
          }

          if (this.passwordMatchTimer) {
            clearTimeout(this.passwordMatchTimer);
          }

          this.setState({
            isSubmitting: false,
            showAllErrors: false,
            errorMessage: '',
            errorMessageShow: false,
            showPassword: false,
            showPasswordConfirmation: false,
            passwordMatchFeedback: null,
          });

          if (onSuccessfulSignUp) {
            onSuccessfulSignUp();
          }

          return;
        }

        const errorMessage =
          response.error ||
          response.errors?.join(', ') ||
          'Sign up failed. Please try again.';

        this.setState({
          errorMessage,
          errorMessageShow: true,
          isSubmitting: false,
        });
      })
      .catch((error) => {
        console.error('Signup request failed:', error);

        this.setState({
          errorMessage: 'Unable to create account. Please try again.',
          errorMessageShow: true,
          isSubmitting: false,
        });
      });
  };

  togglePasswordVisibility = () => {
    this.setState((previousState) => ({
      showPassword: !previousState.showPassword,
    }));
  };

  togglePasswordConfirmationVisibility = () => {
    this.setState((previousState) => ({
      showPasswordConfirmation: !previousState.showPasswordConfirmation,
    }));
  };

  schedulePasswordMatchFeedback = (password, passwordConfirmation) => {
    if (this.passwordMatchTimer) {
      clearTimeout(this.passwordMatchTimer);
    }

    this.setState({
      passwordMatchFeedback: null,
    });

    this.passwordMatchTimer = setTimeout(() => {
      const hasPassword = Boolean(password);
      const hasPasswordConfirmation = Boolean(passwordConfirmation);

      if (!hasPassword || !hasPasswordConfirmation) {
        this.setState({
          passwordMatchFeedback: null,
        });

        return;
      }

      this.setState({
        passwordMatchFeedback:
          password === passwordConfirmation ? 'match' : 'mismatch',
      });
    }, 900);
  };

  renderFieldError = (error) => {
    if (error && this.state.showAllErrors) {
      return (
        <p className="mt-2 text-xs font-semibold text-rose-200">
          {error}
        </p>
      );
    }

    return null;
  };

  renderPasswordMatchMessage = () => {
    if (!this.state.passwordMatchFeedback) return null;

    if (this.state.passwordMatchFeedback === 'mismatch') {
      return (
        <div className="mt-4 rounded-2xl border border-rose-300/25 bg-rose-500/10 px-4 py-3">
          <p className="text-xs font-bold leading-5 text-rose-100">
            Your passwords do not match.
          </p>
        </div>
      );
    }

    return (
      <div className="mt-4 rounded-2xl border border-emerald-300/20 bg-emerald-400/10 px-4 py-3">
        <p className="text-xs font-bold leading-5 text-emerald-100">
          Passwords match.
        </p>
      </div>
    );
  };

  renderPasswordEyeIcon = (isVisible) => {
    if (isVisible) {
      return (
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          className="h-5 w-5"
          fill="none"
        >
          <path
            d="M3 3L21 21"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M10.58 10.58A2 2 0 0 0 13.42 13.42"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M9.88 5.18A10.4 10.4 0 0 1 12 5C17.5 5 21 12 21 12A17.8 17.8 0 0 1 18.84 15.18"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M6.61 6.61C4.42 8.09 3 12 3 12S6.5 19 12 19A9.8 9.8 0 0 0 16.13 18.1"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }

    return (
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="h-5 w-5"
        fill="none"
      >
        <path
          d="M2.5 12S6 5 12 5S21.5 12 21.5 12S18 19 12 19S2.5 12 2.5 12Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path
          d="M12 15A3 3 0 1 0 12 9A3 3 0 0 0 12 15Z"
          stroke="currentColor"
          strokeWidth="2"
        />
      </svg>
    );
  };

  renderLegalAgreement = (errors) => {
    return (
      <Field name="acceptedTerms" type="checkbox">
        {({ input }) => (
          <div
            className={[
              'mt-5 rounded-2xl border px-4 py-4 transition',
              errors.acceptedTerms && this.state.showAllErrors
                ? 'border-rose-300/35 bg-rose-500/10'
                : 'border-cyan-300/15 bg-cyan-400/[0.04]',
            ].join(' ')}
          >
            <label className="flex cursor-pointer items-start gap-3">
              <input
                {...input}
                type="checkbox"
                checked={Boolean(input.checked)}
                className="mt-1 h-4 w-4 rounded border-cyan-300/40 bg-slate-950 text-cyan-400 focus:ring-cyan-400"
              />

              <span className="text-xs font-semibold leading-6 text-slate-300">
                I have read and agree to SubtitleTsunami&apos;s{' '}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-extrabold text-cyan-200 underline decoration-cyan-300/50 underline-offset-4 transition hover:text-cyan-50 hover:decoration-cyan-100"
                  onClick={(event) => event.stopPropagation()}
                >
                  Terms of Service
                </a>
                ,{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-extrabold text-cyan-200 underline decoration-cyan-300/50 underline-offset-4 transition hover:text-cyan-50 hover:decoration-cyan-100"
                  onClick={(event) => event.stopPropagation()}
                >
                  Privacy Policy
                </a>
                , and{' '}
                <a
                  href="/copyright"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-extrabold text-cyan-200 underline decoration-cyan-300/50 underline-offset-4 transition hover:text-cyan-50 hover:decoration-cyan-100"
                  onClick={(event) => event.stopPropagation()}
                >
                  Copyright Policy
                </a>
                .
              </span>
            </label>

            <p className="mt-3 text-xs leading-5 text-slate-400">
              These links open in a new tab so you can review them before
              creating your account.
            </p>

            {this.renderFieldError(errors.acceptedTerms)}
          </div>
        )}
      </Field>
    );
  };

  renderSubmitButton = (values, errors, form) => {
    const hasRequiredValues = Boolean(
      values.email &&
      values.username &&
      values.password &&
      values.passwordConfirmation &&
      values.acceptedTerms
    );

    const hasErrors = Object.keys(errors).length > 0;
    const isDisabled = this.state.isSubmitting;

    return (
      <div className="mt-5">
        {this.state.errorMessageShow && (
          <div className="mb-4 rounded-2xl border border-rose-300/20 bg-rose-500/10 px-4 py-3 text-sm font-semibold leading-5 text-rose-100">
            {this.state.errorMessage}
          </div>
        )}

        <button
          onClick={(event) => {
            event.preventDefault();

            if (!hasErrors && hasRequiredValues) {
              this.onSubmit(values, form);
            } else {
              this.setState({ showAllErrors: true });
            }
          }}
          disabled={isDisabled}
          type="submit"
          className="group relative inline-flex min-h-[50px] w-full items-center justify-center overflow-hidden rounded-2xl border border-emerald-300/30 bg-emerald-400/15 px-5 py-3 text-sm font-black uppercase tracking-[0.14em] text-emerald-100 shadow-lg shadow-emerald-950/30 transition hover:-translate-y-0.5 hover:border-lime-200/70 hover:bg-lime-300/20 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
        >
          <span className="absolute inset-0 translate-x-[-120%] bg-gradient-to-r from-transparent via-emerald-200/20 to-transparent transition duration-700 group-hover:translate-x-[120%]" />

          {this.state.isSubmitting ? (
            <span className="relative inline-flex items-center gap-3">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-emerald-100/30 border-t-emerald-100" />
              Creating Account
            </span>
          ) : (
            <span className="relative">
              Create Account
            </span>
          )}
        </button>
      </div>
    );
  };

  render() {
    return (
      <div className="p-4 sm:p-5">
        <div>
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-emerald-300/20 bg-emerald-400/10 text-xl shadow-lg shadow-emerald-950/20">
            🌊
          </div>

          <p className="mt-4 text-xs font-black uppercase tracking-[0.32em] text-emerald-300">
            Beta Account
          </p>

          <h2 className="mt-2 text-2xl font-black tracking-tight text-white">
            Create your account
          </h2>

          <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-300">
            Start with 1 free 60-second video per day. No card needed.
          </p>
        </div>

        <Form
          validate={(values) => {
            const errors = {};
            const normalizedEmail = this.normalizeEmail(values.email || '');

            if (!normalizedEmail) {
              errors.email = 'Email is empty';
            } else if (!this.isValidEmail(normalizedEmail)) {
              errors.email = 'Enter a valid email address like name@example.com';
            } else if (normalizedEmail.length > 255) {
              errors.email = 'Email is too long';
            }

            if (!values.username) {
              errors.username = 'Username is empty';
            } else if (values.username.length < 3) {
              errors.username = 'Username must be at least 3 characters';
            } else if (values.username.length > 20) {
              errors.username = 'Username is too long';
            } else if (!/^[a-zA-Z0-9_]+$/.test(values.username)) {
              errors.username = 'Username can only use letters, numbers, and underscores';
            }

            if (!values.password) {
              errors.password = 'Password is empty';
            } else if (values.password.length < 6) {
              errors.password = 'Password must be at least 6 characters';
            } else if (values.password.length > 50) {
              errors.password = 'Password is too long';
            }

            if (!values.passwordConfirmation) {
              errors.passwordConfirmation = 'Please confirm your password';
            } else if (values.password !== values.passwordConfirmation) {
              errors.passwordConfirmation = 'Your passwords do not match';
            }

            if (!values.acceptedTerms) {
              errors.acceptedTerms =
                'Please read and agree to the Terms, Privacy Policy, and Copyright Policy.';
            }

            return errors;
          }}
          onSubmit={this.onSubmit}
          render={({ handleSubmit, values, errors, form }) => (
            <form onSubmit={handleSubmit} className="mt-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                    Email
                  </label>

                  <Field name="email">
                    {({ input }) => (
                      <input
                        {...input}
                        placeholder="you@example.com"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck="false"
                        maxLength="255"
                        onChange={(event) => {
                          const cleanedEmail = this.cleanEmailInput(
                            event.target.value
                          );

                          input.onChange(cleanedEmail);
                        }}
                        onBlur={(event) => {
                          const normalizedEmail = this.normalizeEmail(
                            event.target.value
                          );

                          input.onChange(normalizedEmail);
                          input.onBlur(event);
                        }}
                        className={[
                          'w-full rounded-2xl border bg-slate-950/70 px-4 py-3 text-sm font-semibold text-white outline-none transition placeholder:text-slate-600 focus:bg-slate-950/90',
                          errors.email && this.state.showAllErrors
                            ? 'border-rose-300/50 focus:border-rose-200'
                            : 'border-white/10 focus:border-emerald-300/60',
                        ].join(' ')}
                      />
                    )}
                  </Field>

                  {this.renderFieldError(errors.email)}
                </div>

                <div>
                  <label className="mb-2 block text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                    Username
                  </label>

                  <Field name="username">
                    {({ input }) => (
                      <input
                        {...input}
                        placeholder="Choose a username"
                        autoComplete="username"
                        maxLength="20"
                        className={[
                          'w-full rounded-2xl border bg-slate-950/70 px-4 py-3 text-sm font-semibold text-white outline-none transition placeholder:text-slate-600 focus:bg-slate-950/90',
                          errors.username && this.state.showAllErrors
                            ? 'border-rose-300/50 focus:border-rose-200'
                            : 'border-white/10 focus:border-emerald-300/60',
                        ].join(' ')}
                      />
                    )}
                  </Field>

                  {this.renderFieldError(errors.username)}
                </div>

                <div>
                  <label className="mb-2 block text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                    Password
                  </label>

                  <Field name="password">
                    {({ input }) => (
                      <div className="relative">
                        <input
                          {...input}
                          placeholder="Create a password"
                          type={this.state.showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          maxLength="50"
                          onChange={(event) => {
                            input.onChange(event);

                            const nextPassword = event.target.value;
                            const currentValues = form.getState().values || {};
                            const currentPasswordConfirmation =
                              currentValues.passwordConfirmation || '';

                            this.schedulePasswordMatchFeedback(
                              nextPassword,
                              currentPasswordConfirmation
                            );
                          }}
                          className={[
                            'w-full rounded-2xl border bg-slate-950/70 px-4 py-3 pr-12 text-sm font-semibold text-white outline-none transition placeholder:text-slate-600 focus:bg-slate-950/90',
                            errors.password && this.state.showAllErrors
                              ? 'border-rose-300/50 focus:border-rose-200'
                              : 'border-white/10 focus:border-emerald-300/60',
                          ].join(' ')}
                        />

                        <button
                          type="button"
                          onClick={this.togglePasswordVisibility}
                          aria-label={
                            this.state.showPassword
                              ? 'Hide password'
                              : 'Show password'
                          }
                          className={[
                            'absolute right-3 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-xl border text-slate-400 transition',
                            this.state.showPassword
                              ? 'border-emerald-300/30 bg-emerald-400/10 text-emerald-200'
                              : 'border-white/10 bg-white/[0.03] hover:border-emerald-300/30 hover:bg-emerald-400/10 hover:text-emerald-200',
                          ].join(' ')}
                        >
                          {this.renderPasswordEyeIcon(this.state.showPassword)}
                        </button>
                      </div>
                    )}
                  </Field>

                  {this.renderFieldError(errors.password)}
                </div>

                <div>
                  <label className="mb-2 block text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                    Confirm Password
                  </label>

                  <Field name="passwordConfirmation">
                    {({ input }) => (
                      <div className="relative">
                        <input
                          {...input}
                          placeholder="Confirm your password"
                          type={
                            this.state.showPasswordConfirmation
                              ? 'text'
                              : 'password'
                          }
                          autoComplete="new-password"
                          maxLength="50"
                          onChange={(event) => {
                            input.onChange(event);

                            const nextPasswordConfirmation = event.target.value;
                            const currentValues = form.getState().values || {};
                            const currentPassword = currentValues.password || '';

                            this.schedulePasswordMatchFeedback(
                              currentPassword,
                              nextPasswordConfirmation
                            );
                          }}
                          className={[
                            'w-full rounded-2xl border bg-slate-950/70 px-4 py-3 pr-12 text-sm font-semibold text-white outline-none transition placeholder:text-slate-600 focus:bg-slate-950/90',
                            errors.passwordConfirmation && this.state.showAllErrors
                              ? 'border-rose-300/50 focus:border-rose-200'
                              : 'border-white/10 focus:border-emerald-300/60',
                          ].join(' ')}
                        />

                        <button
                          type="button"
                          onClick={this.togglePasswordConfirmationVisibility}
                          aria-label={
                            this.state.showPasswordConfirmation
                              ? 'Hide password confirmation'
                              : 'Show password confirmation'
                          }
                          className={[
                            'absolute right-3 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-xl border text-slate-400 transition',
                            this.state.showPasswordConfirmation
                              ? 'border-emerald-300/30 bg-emerald-400/10 text-emerald-200'
                              : 'border-white/10 bg-white/[0.03] hover:border-emerald-300/30 hover:bg-emerald-400/10 hover:text-emerald-200',
                          ].join(' ')}
                        >
                          {this.renderPasswordEyeIcon(
                            this.state.showPasswordConfirmation
                          )}
                        </button>
                      </div>
                    )}
                  </Field>

                  {this.renderFieldError(errors.passwordConfirmation)}
                </div>
              </div>

              {this.renderPasswordMatchMessage()}

              {this.renderLegalAgreement(errors)}

              {this.renderSubmitButton(values, errors, form)}
            </form>
          )}
        />
      </div>
    );
  }
}

export default SignUpBox;